import React, { useMemo } from 'react';
import { formatNumber, formatRank } from '../../utils/formatting';
import { getScoreColor } from '../../utils/coloring';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import './Tables.css';

const HistoricalScoresTable = ({ 
  selectedLocation, 
  selectedCrime, 
  selectedDateRange,
  setSelectedDateRange,
  setIsHistorical,
  compareMode
}) => {
  const { info, data } = selectedLocation;
  const locationType = info?.type?.charAt(0).toUpperCase() + info?.type?.slice(1);
  const isNation = info?.type?.toLowerCase() === 'nation';
  const isCounty = info?.type?.toLowerCase() === 'county';
  const isCity = info?.type?.toLowerCase() === 'city';
  const isCountyOrCity = isCounty || isCity;
  
  const stateAbbr = useMemo(() => {
    if (isCountyOrCity && info?.name?.includes(',')) {
      const stateName = info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, info?.name, selectedLocation]);

  if (isNation) {
    return (
      <div className="loading">
        Scores are not available for the entire nation
      </div>
    );
  }

  const handleYearClick = (year) => {
    setSelectedDateRange(year.toString());
    setTimeout(() => {
      setIsHistorical(false);
    }, 100);
  };

  const transformData = () => {
    if (!data?.historical) {
      return [];
    }

    return data.historical
      .sort((a, b) => b.year - a.year)
      .map(yearData => {
        const baseScoreField = `${selectedCrime.toLowerCase()}_score`;
        
        const scoreField = isCountyOrCity
          ? compareMode === 'state'
            ? `${selectedCrime.toLowerCase()}_state_score`
            : `${selectedCrime.toLowerCase()}_national_score`
          : baseScoreField;
        
        const score = yearData[scoreField];
        
        if (score === null || score === undefined) return null;

        const rankField = isCountyOrCity
          ? compareMode === 'state'
            ? `${selectedCrime.toLowerCase()}_state_score_rank`
            : `${selectedCrime.toLowerCase()}_national_score_rank`
          : `${selectedCrime.toLowerCase()}_score_rank`;
        
        const compositeScoreField = isCountyOrCity
          ? compareMode === 'state'
            ? 'state_composite_score'
            : 'national_composite_score'
          : 'composite_score';
        
        const compositeRankField = isCountyOrCity
          ? compareMode === 'state'
            ? 'state_composite_score_rank'
            : 'national_composite_score_rank'
          : 'composite_score_rank';

        return {
          year: yearData.year,
          score,
          compositeScore: yearData[compositeScoreField],
          compositeScoreRank: yearData[compositeRankField],
          rank: yearData[rankField],
          stateScore: isCountyOrCity ? yearData[`${selectedCrime.toLowerCase()}_state_score`] : null,
          nationalScore: isCountyOrCity ? yearData[`${selectedCrime.toLowerCase()}_national_score`] : null,
          stateRank: isCountyOrCity ? yearData[`${selectedCrime.toLowerCase()}_state_score_rank`] : null,
          nationalRank: isCountyOrCity ? yearData[`${selectedCrime.toLowerCase()}_national_score_rank`] : null,
          stateCompositeScore: isCountyOrCity ? yearData['state_composite_score'] : null,
          nationalCompositeScore: isCountyOrCity ? yearData['national_composite_score'] : null,
          stateCompositeRank: isCountyOrCity ? yearData['state_composite_score_rank'] : null,
          nationalCompositeRank: isCountyOrCity ? yearData['national_composite_score_rank'] : null,
        };
      })
      .filter(row => row !== null);
  };

  const tableData = transformData();

  if (!tableData.length) {
    return <div className="loading">No Historical Data Available</div>;
  }

  return (
    <div className="main-table-wrapper">
      <div className="main-table-top">
        {selectedCrime === 'all_crime' ? 'All' : ''}&nbsp;{getCrimeDisplayName(selectedCrime)}&nbsp;Historical Scores
      </div>
      <div className="main-table-scroll-container">
        <table className="main-table scores-table historical-table">
          <thead>
            <tr>
              <th>Year</th>
              <th>
                <div className="stacked-header">
                  <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} KRIME Score&trade;</div>
                  <div>(Composite)</div>
                </div>
              </th>
              <th>
                <div className="stacked-header">
                  <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Rank</div>
                  <div>(Composite)</div>
                </div>
              </th>
              <th>
                <div className="stacked-header">
                  <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {selectedCrime === 'all_crime' ? 'All' : ''} {getCrimeDisplayName(selectedCrime)}</div>
                  <div>Score (Non-Weighted)</div>
                </div>
              </th>
              <th>
                <div className="stacked-header">
                  <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Rank</div>
                  <div>(Non-Weighted)</div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row) => {
              const displayScore = row.score;
              const displayRank = row.rank;
              const displayCompositeScore = row.compositeScore;
              const displayCompositeRank = row.compositeScoreRank;
              
              return (
                <tr 
                  key={row.year} 
                  className={row.year === parseInt(selectedDateRange) ? 'selected-row' : ''}
                >
                  <td>
                    <span onClick={() => handleYearClick(row.year)}>
                      {row.year}
                    </span>
                  </td>
                  <td className={`score-cell bold`} style={{ color: getScoreColor(displayCompositeScore) }}>
                    {formatNumber(displayCompositeScore, true)}
                  </td>
                  <td className="rank-cell">{formatRank(displayCompositeRank)}</td>
                  <td className={`score-cell bold`} style={{ color: getScoreColor(displayScore) }}>
                    <em>{formatNumber(displayScore, true)}</em>
                  </td>
                  <td className="rank-cell"><em>{formatRank(displayRank)}</em></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HistoricalScoresTable;